import { useState, useCallback, useRef } from 'react';
import {
  useEditdiseaseLabTable,
  useEditWaterVibrioLabTable,
  useEditShrimpHealthLabTable,
} from '../../../api/mutation';
import { isDiseaseGroup, buildLabCellPatch, generateCellKey } from '../utils';
import { SUB_GROUPS } from '../constants';

interface CellChangeParams {
  group: string;
  subGroup?: string;
  label: string;
  id: number;
  columnIndex: number;
  value: string;
}

export const useCellEditor = () => {
  const [editedCells, setEditedCells] = useState<Map<string, string>>(new Map());
  const editedCellsRef = useRef<Map<string, string>>(new Map());
  // NK-305: saves of one record run one after another, so a slow request can
  // never land after a newer one for the same cell.
  const queuesRef = useRef<Map<string, Promise<unknown>>>(new Map());

  const editDiseaseMutation = useEditdiseaseLabTable();
  const editWaterVibrioMutation = useEditWaterVibrioLabTable();
  const editShrimpHealthMutation = useEditShrimpHealthLabTable();

  // Mutation objects change identity every render; read them through a ref so
  // handleCellChange stays stable (EditableCell's memo ignores onChange).
  const mutationsRef = useRef({ editDiseaseMutation, editWaterVibrioMutation, editShrimpHealthMutation });
  mutationsRef.current = { editDiseaseMutation, editWaterVibrioMutation, editShrimpHealthMutation };

  const handleCellChange = useCallback(async (params: CellChangeParams) => {
    const key = generateCellKey(params.group, params.subGroup, params.label, params.id);

    const newMap = new Map<string, string>(editedCellsRef.current);
    newMap.set(key, params.value);
    editedCellsRef.current = newMap;
    setEditedCells(newMap);

    // NK-305: send only the edited field. The whole-column body used to carry
    // the other cells' values too, and a stale one overwrote a newer save.
    const body = buildLabCellPatch(params.id, params.subGroup, params.label, params.value);
    if (!body) return;

    const { editDiseaseMutation, editWaterVibrioMutation, editShrimpHealthMutation } = mutationsRef.current;
    let mutation: typeof editDiseaseMutation | undefined;
    let endpoint = '';
    if (isDiseaseGroup(params.group)) {
      mutation = editDiseaseMutation;
      endpoint = 'treatment';
    } else if (params.subGroup === SUB_GROUPS.WATER) {
      mutation = editWaterVibrioMutation;
      endpoint = 'water-vibrio';
    } else if (params.subGroup === SUB_GROUPS.LIVER || params.subGroup === SUB_GROUPS.STOMACH) {
      mutation = editShrimpHealthMutation;
      endpoint = 'shrimp-health';
    }
    if (!mutation) return;

    const queueKey = `${endpoint}_${params.id}`;
    const send = mutation.mutateAsync;
    const previous = queuesRef.current.get(queueKey) ?? Promise.resolve();
    const task = previous.catch(() => undefined).then(() => send(body));
    queuesRef.current.set(queueKey, task);

    try {
      await task;
    } catch {
      // Revert on error — unless the cell has been edited again since, in which
      // case the newer value stays for its own save.
      if (editedCellsRef.current.get(key) === params.value) {
        const revertedMap = new Map<string, string>(editedCellsRef.current);
        revertedMap.delete(key);
        editedCellsRef.current = revertedMap;
        setEditedCells(revertedMap);
      }
    } finally {
      if (queuesRef.current.get(queueKey) === task) queuesRef.current.delete(queueKey);
    }
  }, []);

  return { editedCells, handleCellChange };
};
